import styled from '@emotion/styled';
import { format } from 'date-fns/format';
import { colorsButton } from 'app/colors';
import { borderRadii } from 'app/spacing';
import { KizenTypography, fontWeights } from 'app/typography';
import { TextEllipsisWithTooltip, TextEllipsis } from 'components/Kizen/Table';
import { isPast } from 'utility/datetime';
import { EVENT_TYPES } from 'pages/Broadcasts/constants';
import IconButtonAbsoluteMenu from 'components/Tables/Big/IconButtonAbsoluteMenu';
import Icon from 'components/Kizen/Icon';
import { useTranslation } from 'react-i18next';
import css from '@emotion/css';

const Container = styled.div`
  position: relative;
  cursor: default;
  height: 21px;
  width: 100%;
  border-radius: ${borderRadii.tiny};
  border: 1px solid
    ${({ isInvalid }) =>
      isInvalid
        ? 'var(--color-button-warning-hover)'
        : 'var(--color-border-medium)'};

  ${({ enableHoverEffect }) =>
    enableHoverEffect &&
    `
    &:hover {
      background-color: var(--color-background-light);
    }
    &:hover .menu-button-wrapper {
      opacity: 1;
    }
  `}
`;

const TextContainer = styled.div`
  display: flex;
  padding: 5px 0;

  ${({ isSpanning }) =>
    isSpanning
      ? css`
          && * {
            font-style: italic;
            color: var(--color-font-secondary) !important;
          }
        `
      : ''}
`;

const TimeText = styled(KizenTypography)`
  flex-shrink: 0;
  margin: 0 5px;
  font-weight: ${fontWeights.bold};
  color: ${({ past, isInvalid }) =>
    isInvalid
      ? 'var(--color-button-warning-hover)'
      : past && 'var(--color-font-placeholder)'};
`;

const TypeTextWithTooltip = styled(TextEllipsisWithTooltip)`
  margin-top: 0;
  padding-right: 5px;
  color: ${({ past, isInvalid }) =>
    isInvalid
      ? 'var(--color-button-warning-hover)'
      : past && 'var(--color-font-placeholder)'};
`;

const TypeTextNoTooltip = styled(TextEllipsis)`
  margin-top: 0;
  padding-right: 5px;
  color: ${({ past, isInvalid }) =>
    isInvalid
      ? 'var(--color-button-warning-hover)'
      : past && 'var(--color-font-placeholder)'};
`;

const MenuButtonWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 0 2px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 1;
  background: linear-gradient(
    to left,
    var(--color-background-white) 60%,
    color-mix(in srgb, var(--color-background-white) 0%, transparent) 100%
  );
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const LeftBar = styled.div`
  position: absolute;
  top: -1px;
  left: -1px;
  height: calc(100% + 2px);
  width: 2px;
  border-top-left-radius: ${borderRadii.tiny};
  border-bottom-left-radius: ${borderRadii.tiny};
  background-color: ${({ type, past, isInvalid }) =>
    isInvalid
      ? 'var(--color-button-warning-hover)'
      : past
        ? EVENT_TYPES.default.color
        : EVENT_TYPES[type]?.color || EVENT_TYPES.default.color};
`;

export const CalendarEventPill = ({
  isInvalid,
  enableHoverEffect = false,
  disableTooltip = false,
  type,
  past,
  children,
  date,
  isSpanning,
  onHover,
  id,
  allDay = false,
  url,
  ...rest
}) => {
  const TypeText = disableTooltip ? TypeTextNoTooltip : TypeTextWithTooltip;

  const { t } = useTranslation();

  return (
    <Container
      type={type}
      past={past}
      isInvalid={isInvalid}
      enableHoverEffect={enableHoverEffect}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
      data-activity-id={id}
      {...rest}
    >
      <LeftBar past={past} type={type} isInvalid={isInvalid} />
      <TextContainer>
        {!isSpanning ? (
          <TimeText past={past} isInvalid={isInvalid} size="buttonLabel">
            {allDay
              ? t('All Day')
              : date
                ? `${format(date, 'h:mm')}${format(date, 'a')[0]}`
                : null}
          </TimeText>
        ) : (
          <TimeText />
        )}
        <TypeText
          past={past}
          size="buttonLabel"
          noSpace
          data-qa-name={name}
          isInvalid={isInvalid}
        >
          {children} {isSpanning ? t('(continued)') : null}
        </TypeText>
      </TextContainer>
    </Container>
  );
};

const EventPill = ({
  date,
  type,
  name,
  isInvalid,
  isPastDate = false,
  disableTooltip = false,
  showActionMenu = false,
  actionMenuOptions = [],
  onActionMenuSelect,
  actionMenuTitle,
  actionMenuDataQaId,
  enableHoverEffect = false,
  children,
  isSpanning = false,
  allDay = false,
  ...rest
}) => {
  const past = date ? isPast(date) : isPastDate;
  const TypeText = disableTooltip ? TypeTextNoTooltip : TypeTextWithTooltip;

  const { t } = useTranslation();

  return (
    <Container
      type={type}
      past={past}
      isInvalid={isInvalid}
      enableHoverEffect={enableHoverEffect}
      {...rest}
    >
      <LeftBar past={past} type={type} isInvalid={isInvalid} />
      <TextContainer isSpanning={isSpanning}>
        {!isSpanning ? (
          <TimeText past={past} isInvalid={isInvalid} size="buttonLabel">
            {allDay
              ? t('All Day')
              : date
                ? `${format(date, 'h:mm')}${format(date, 'a')[0]}`
                : null}
          </TimeText>
        ) : (
          <TimeText />
        )}
        <TypeText
          past={past}
          size="buttonLabel"
          noSpace
          data-qa-name={name}
          isInvalid={isInvalid}
        >
          {name} {isSpanning ? t('(continued)') : null}
        </TypeText>
      </TextContainer>
      {showActionMenu && (
        <MenuButtonWrapper
          className="menu-button-wrapper"
          onClick={(e) => e.stopPropagation()}
        >
          <IconButtonAbsoluteMenu
            color={colorsButton.iconGray}
            onChange={onActionMenuSelect}
            options={actionMenuOptions}
            position="right"
            data-qa-id={actionMenuDataQaId}
          >
            <Icon icon="three-dot" />
          </IconButtonAbsoluteMenu>
        </MenuButtonWrapper>
      )}
      {children}
    </Container>
  );
};

export default EventPill;
